import {
  useTxnEnquiry,
  useEarnNRegister,
  useCheckMemberAndOtp,
  txnResponse,
  checkMemberRes,
  formatDateTime,
  getDeeplinkToMerchantPage,
} from "@/app/api"
import { changeEarnPointResponse, changePhone } from "@/app/appSlice"
import { RootState } from "@/app/store"

export default function Phone() {
  const t = useTranslate()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const earnNRegister = useEarnNRegister()
  const checkMember = useCheckMemberAndOtp()
  const txnEnquiry = useTxnEnquiry()

  const [countryCode, setCountryCode] = useState("852")
  const [maxDigits, setMaxDigits] = useState(8)
  const [phone, setPhone] = useState("")
  const [buttonEnabled, setButtonEnabled] = useState(false)
  const [errMsg, setErrMsg] = useState("")
  const [result, setResult] = useState<txnResponse | null>(null)

  const txnGA = useAppSelector((state: RootState) => {
    return { ...state.app.txnGA, invoiceQRCode: state.app.invoiceQRCode }
  })

  useEffect(() => {
    ;(async () => {
      const res = await txnEnquiry()
      window.gtag("event", "onboard_page", {
        brandName: res?.brandName,
        brandId: res?.brandId,
        amount: res?.transactionAmount,
        points: res?.pointChange,
      })
      setResult(res)
    })()
  }, [])

  const next = async () => {
    if (!result?.brandId) return
    let res: string | checkMemberRes | null = null
    try {
      res = await checkMember({
        phone: `${countryCode}${phone}`,
        brandId: result.brandId,
      })
    } catch (e: any) {
      setErrMsg(e)
      return
    }

    dispatch(changePhone(`${countryCode}${phone}`))
    if (typeof res === "string" && res === "OTP") {
      window.gtag("event", "submit_phone", { registered: false, ...txnGA })
      navigate("/otp")
    } else if (typeof res === "string" && res === "REGISTERED") {
      window.gtag("event", "submit_phone", { registered: true, ...txnGA })
      const res = await earnNRegister({
        phone: `${countryCode}${phone}`,
        otp: "",
      })
      if (res) {
        dispatch(changeEarnPointResponse(res))
        navigate("/success")
      }
    }
  }

  const onCountryCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCountryCode(e.target.value)
    setMaxDigits(e.target.value === "86" ? 11 : 8)
    setPhone("")
    setButtonEnabled(false)
  }

  const onPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isNaN(Number(e.target.value))) return
    setPhone(e.target.value)
    setButtonEnabled(e.target.value.length === maxDigits)
  }

  return (
    result && (
      <>
        <div className="body">
          <div className="receipt">
            <div className="row-center">
              <img src={result.brandLogo} alt="icon" className="icon" />
              <h1 className="text1">{result.brandName}</h1>
            </div>
            <p className="text1 mt-4">{t("thanksForPurchasing")}</p>
            <p className="text2">
              {t("transactionDate")} {formatDateTime(result.transactionDate)}
            </p>
            <div className="row-between mt-4">
              <p className="text2">{t("transactionAmount")}</p>
              <p className="text2">
                {result.pointEarnDate == undefined
                  ? t("pointsAvailable")
                  : t("pointsEarned")}
              </p>
            </div>
            <div className="row-between">
              <p className="amount">${result.transactionAmount}</p>
              <p className="text1">=</p>
              <p className="point">
                {result.pointEarnDate == undefined
                  ? result.pointChange
                  : result.pointEarned}
              </p>
            </div>
          </div>

          {result.pointEarnDate && (
            <>
              <p className="text1 mt-2">{t("registeredSuccessfully")}</p>
              <p className="text2">
                {t("registrationTime")} {formatDateTime(result.transactionDate)}
              </p>
              <p className="text2 mt-4">{t("checkPointBalance")}</p>
              <a href={getDeeplinkToMerchantPage(result.brandId)}>
                <button className="confirm-button">{t("openStorellet")}</button>
              </a>
              <a href={result.customRedirectUrl ?? "https://www.storellet.hk/"}>
                <button className="simple-button">{t("openWebsite")}</button>
              </a>
            </>
          )}

          {!result.pointEarnDate && (
            <>
              <p className="text1 mt-2">{t("registerPointsNow")}</p>
              <div className="flex flex-col mt-4">
                <p className="text2 text-left">{t("phoneNumber")}</p>
                <div className="flex flex-row">
                  <select onChange={onCountryCodeChange}>
                    <option value="852">852</option>
                    <option value="853">853</option>
                    <option value="86">86</option>
                  </select>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={phone}
                    placeholder={t("enterPhoneNumber")}
                    maxLength={maxDigits}
                    onChange={onPhoneChange}
                  />
                </div>
                {errMsg && <p className="text-error text-left">{errMsg}</p>}
                <p className="text2 text-left">
                  {t("checkPhoneNumberCorrectness")}
                </p>
              </div>
              <button
                className="confirm-button"
                onClick={next}
                disabled={!buttonEnabled}
              >
                {t("registerPhones")}
              </button>
            </>
          )}
        </div>
      </>
    )
  )
}
